/**
 * ADM-28 教室管理页（org-campus:SC-33~SC-56 前端）。
 *
 * 筛选区：校区下拉 + 名称输入 + 状态下拉 + 查询/重置。
 * 列表表格：校区、教室名称、容量、未来占用课次数、状态、更新时间、操作（编辑/停用/启用）。
 * 新建/编辑：对话框表单（校区新建可选/编辑只读、名称、容量 InputNumber min=1；编辑携带 version）。
 * 停用/启用：二次确认弹窗。
 */
import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { EditDialog } from "../../components/EditDialog";
import { useResizableColumns } from "../../hooks/useResizableColumns";
import type { ColumnsType } from "antd/es/table";
import {
  classroomsApi,
  type ClassroomItem,
  type ClassroomStatus,
  type CreateClassroom,
  type UpdateClassroom,
} from "../../api/classrooms.api";
import { orgCampusApi, type CampusItem } from "../../api/campuses.api";
import { ApiError } from "../../api/transport";
import { HasPermission, useHasPermission } from "../../auth/HasPermission";

const { Title } = Typography;

/** 教室状态标签映射。 */
const classroomStatusTag: Record<
  ClassroomStatus,
  { color: string; label: string }
> = {
  AVAILABLE: { color: "green", label: "可用" },
  DISABLED: { color: "default", label: "停用" },
};

/** 提取错误信息。 */
function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "操作失败";
}

export function ClassroomsPage() {
  const [data, setData] = useState<ClassroomItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<{
    campusId?: string;
    name?: string;
    status?: ClassroomStatus;
  }>({});
  const [campusOptions, setCampusOptions] = useState<CampusItem[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ClassroomItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<CreateClassroom & { version?: number }>();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await classroomsApi.listClassrooms({
        ...filters,
        page,
        pageSize,
      });
      setData(result.items);
      setTotal(result.total);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  /** 拉取校区下拉选项（仅 ACTIVE，供筛选区与新建弹窗共用；进页即拉，修复筛选下拉初始为空）。 */
  const fetchCampusOptions = useCallback(async () => {
    try {
      const result = await orgCampusApi.listCampuses({
        status: "ACTIVE",
        pageSize: 100,
      });
      setCampusOptions(result.items);
    } catch {
      // 校区下拉加载失败不阻断教室列表
    }
  }, []);

  useEffect(() => {
    void fetchCampusOptions();
    void fetchList();
  }, [fetchCampusOptions, fetchList]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    void fetchCampusOptions();
    setDialogOpen(true);
  };

  const openEdit = (record: ClassroomItem) => {
    setEditing(record);
    form.setFieldsValue({
      campusId: record.campusId,
      name: record.name,
      capacity: record.capacity,
      version: record.version,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      if (editing) {
        const update: UpdateClassroom = {
          name: values.name,
          capacity: values.capacity,
          version: values.version ?? editing.version,
        };
        await classroomsApi.updateClassroom(editing.id, update);
        message.success("教室信息已更新");
      } else {
        const create: CreateClassroom = {
          campusId: values.campusId,
          name: values.name,
          capacity: values.capacity,
        };
        await classroomsApi.createClassroom(create);
        message.success("教室已创建");
      }
      setDialogOpen(false);
      void fetchList();
    } catch (err) {
      if (err instanceof ApiError) {
        message.error(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async (id: string) => {
    try {
      await classroomsApi.disableClassroom(id);
      message.success("教室已停用");
      void fetchList();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  const handleEnable = async (id: string) => {
    try {
      await classroomsApi.enableClassroom(id);
      message.success("教室已启用");
      void fetchList();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  // 行操作统一 EDIT 闸控（对齐后端 @Roles）；无权限整列不渲染，越权由后端 403 兜底
  const canManage = useHasPermission("ADM-28", "EDIT");
  const { columns, components } = useResizableColumns<ClassroomItem>([
    { title: "校区", dataIndex: "campusName", width: 160, ellipsis: true },
    { title: "教室名称", dataIndex: "name", width: 160, ellipsis: true },
    {
      title: "容量",
      dataIndex: "capacity",
      width: 90,
      align: "center",
    },
    {
      title: "未来占用课次数",
      dataIndex: "futureSessionCount",
      width: 120,
      align: "center",
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 90,
      render: (status: ClassroomStatus) => {
        const cfg = classroomStatusTag[status] ?? {
          color: "default",
          label: status,
        };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      width: 160,
      render: (v?: string) => (v ? new Date(v).toLocaleString("zh-CN") : "-"),
    },
    ...(canManage
      ? ([
          {
            title: "操作",
            fixed: "right",
            key: "action",
            width: 160,
            render: (_, record) => (
              <Space size="small">
                <Button
                  type="link"
                  size="small"
                  onClick={() => openEdit(record)}
                >
                  编辑
                </Button>
                {record.status === "AVAILABLE" ? (
                  <Popconfirm
                    title="确定停用该教室吗？"
                    description="停用后不进入新排课选择，历史课次仍可展示"
                    onConfirm={() => handleDisable(record.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" size="small" danger>
                      停用
                    </Button>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title="确定启用该教室吗？"
                    onConfirm={() => handleEnable(record.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" size="small">
                      启用
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ] as ColumnsType<ClassroomItem>)
      : []),
  ]);

  return (
    <div>
      <Title level={4}>教室管理</Title>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="校区"
            allowClear
            value={filters.campusId}
            onChange={(val) =>
              setFilters((f) => ({ ...f, campusId: val ?? undefined }))
            }
            style={{ width: 180 }}
            options={campusOptions.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
          />
          <Input
            placeholder="教室名称"
            allowClear
            value={filters.name ?? ""}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                name: e.target.value || undefined,
              }))
            }
            style={{ width: 180 }}
          />
          <Select
            placeholder="状态"
            allowClear
            value={filters.status}
            onChange={(val) =>
              setFilters((f) => ({ ...f, status: val ?? undefined }))
            }
            style={{ width: 120 }}
            options={[
              { value: "AVAILABLE", label: "可用" },
              { value: "DISABLED", label: "停用" },
            ]}
          />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => {
              void fetchCampusOptions();
              void fetchList();
            }}
          >
            查询
          </Button>
          <Button
            onClick={() => {
              setFilters({});
              setPage(1);
            }}
          >
            重置
          </Button>
        </Space>
      </Card>
      <Card>
        <div style={{ marginBottom: 16 }}>
          {/* 无 ADM-28 CREATE 权限不渲染入口，越权直调由后端 403 兜底 */}
          <HasPermission menuKey="ADM-28" action="CREATE">
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              新建教室
            </Button>
          </HasPermission>
        </div>
        <Table<ClassroomItem>
          scroll={{ x: "max-content" }}
          rowKey="id"
          columns={columns}
          components={components}
          dataSource={data}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>
      <EditDialog
        title={editing ? "编辑教室" : "新建教室"}
        open={dialogOpen}
        onCancel={() => setDialogOpen(false)}
        onOk={() => void handleSubmit()}
        confirmLoading={submitting}
        okText="保存"
        cancelText="取消"
        width={420}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="campusId"
            label="校区"
            rules={[{ required: true, message: "请选择校区" }]}
          >
            <Select
              placeholder="选择校区"
              disabled={!!editing}
              options={campusOptions.map((c) => ({
                value: c.id,
                label: c.name,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="教室名称"
            rules={[{ required: true, message: "请输入教室名称" }]}
          >
            <Input placeholder="如 301教室" />
          </Form.Item>
          <Form.Item
            name="capacity"
            label="容量"
            rules={[{ required: true, message: "请输入容量" }]}
          >
            <InputNumber
              min={1}
              style={{ width: "100%" }}
              placeholder="如 30"
            />
          </Form.Item>
        </Form>
      </EditDialog>
    </div>
  );
}
